import { HTMLAttributes, useCallback, useRef, useState } from 'react'
import gsap from 'gsap'

import Modal from '@/components/base/modal'
import SVGIcon from '@/components/base/svg-icon'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'
import { WPFile } from '@/types'

type MagneticVideoProps = HTMLAttributes<HTMLDivElement> & {
  video?: WPFile
  video_preview?: WPFile
}

const MagneticVideo = ({
  className,
  video,
  video_preview,
  ...props
}: MagneticVideoProps) => {
  const { strings } = useStore()

  const rootRef = useRef<HTMLDivElement>(null)
  const videoRef = useRef<HTMLDivElement>(null)
  const playRef = useRef<HTMLDivElement>(null)
  const modalVideoRef = useRef<HTMLVideoElement | null>(null)

  const [modalOpen, setModalOpen] = useState(false)

  const handlePointerMove: React.PointerEventHandler<HTMLDivElement> = (
    event
  ) => {
    const root = rootRef.current
    const video = videoRef.current
    const button = playRef.current

    if (!root || !video || !button) return

    const rect = root.getBoundingClientRect()

    const x = event.clientX - rect.left
    const y = event.clientY - rect.top

    const centerX = rect.width / 2
    const centerY = rect.height / 2

    const distanceFromCenterX = x - centerX
    const distanceFromCenterY = y - centerY

    // valore normalizzato tra -1 e 1
    const progressX = distanceFromCenterX / centerX
    const progressY = distanceFromCenterY / centerY

    // intensità del magnete
    const videoMoveX = progressX * 24
    const videoMoveY = progressY * 24

    const buttonMoveX = progressX * 13
    const buttonMoveY = progressY * 13

    gsap.to(video, {
      x: videoMoveX,
      y: videoMoveY,
      scale: 1.1,
      duration: 0.35,
      ease: 'power3.out',
      overwrite: true
    })

    gsap.to(button, {
      x: buttonMoveX,
      y: buttonMoveY,
      scale: 0.7,
      duration: 0.5,
      ease: 'power3.out',
      overwrite: true
    })
  }

  const handlePointerLeave = () => {
    const video = videoRef.current
    const button = playRef.current

    if (!video || !button) return

    gsap.to(video, {
      x: 0,
      y: 0,
      scale: 1,
      duration: 0.9,
      ease: 'elastic.out(1, 0.5)',
      overwrite: true
    })

    gsap.to(button, {
      x: 0,
      y: 0,
      scale: 1,
      duration: 0.9,
      ease: 'elastic.out(1, 0.45)',
      overwrite: true
    })
  }

  const openModalVideo = useCallback(() => {
    if (!video?.url) return
    setModalOpen(true)
  }, [video])

  const closeModalVideo = useCallback(() => {
    const videoEl = modalVideoRef.current
    if (videoEl) {
      videoEl.pause()
    }
    setModalOpen(false)
  }, [])

  const handleModalVideoRef = useCallback(
    (el: HTMLVideoElement | null) => {
      modalVideoRef.current = el
      if (el && modalOpen) {
        el.play().catch(() => {
          // autoplay con audio può essere bloccato dal browser
        })
      }
    },
    [modalOpen]
  )

  return (
    <div
      ref={rootRef}
      data-layout="magnetic-video"
      className={cn(
        'relative',
        'before:absolute before:inset-0 before:-z-10 before:scale-[1.5] before:rounded-full hover:before:scale-[2.2]',
        className
      )}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
      {...props}
    >
      <div
        ref={videoRef}
        className={cn(
          'group',
          'size-full rounded-full',
          'after:absolute after:inset-0 after:-z-10 after:rounded-full after:opacity-0 after:shadow-[2px_10px_20px_-4px_rgba(0,0,0,0.4)] after:transition-opacity after:duration-100 hover:after:opacity-100'
        )}
      >
        <button
          className={cn('size-full overflow-hidden rounded-full')}
          onClick={openModalVideo}
        >
          <video
            autoPlay
            loop
            muted
            playsInline
            className="size-full object-cover"
            src={video_preview?.url || video?.url}
          />
          <div
            ref={playRef}
            className={cn(
              'focus:ring-white focus:ring-offset-gray-900 absolute left-1/2 top-1/2 flex size-[72px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[black] text-[white] focus:outline-none focus:ring-2 focus:ring-offset-2'
            )}
          >
            <span className="sr-only">
              {strings?.labels?.play_video || 'play video'}
            </span>
            <SVGIcon symbol="play" className="size-[24px]" />
          </div>
        </button>
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModalVideo}
        closeLabel={strings?.labels?.close || 'Chiudi'}
      >
        <video
          ref={handleModalVideoRef}
          src={video?.url}
          controls
          playsInline
          className="relative z-10 h-full w-full"
          onEnded={closeModalVideo}
        />
      </Modal>
    </div>
  )
}

export default MagneticVideo
